import React, { useEffect, useState } from 'react';
import {
  Network,
  RefreshCw,
  Save,
  FlaskConical,
  Search,
  Database,
  Cloud,
  RotateCw,
  Eye,
  X,
} from 'lucide-react';
import { adminPlatformAPI } from '../services/adminPlatformApi';
import { ACCOUNT_TYPES } from '../constants/platformRoles';

type TabIndex = 0 | 1 | 2 | 3;

const TABS = ['Connections', 'Configuration', 'Entity Browser', 'Sync Tools'] as const;

const AdminPlatform: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabIndex>(0);
  const [platformStatus, setPlatformStatus] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const [form, setForm] = useState({
    enabled: false,
    cbUrl: '',
    service: 'cerere',
    servicePath: '/t44',
    apiKey: '',
    subscriptionCallbackUrl: '',
  });
  const [hasApiKey, setHasApiKey] = useState(false);
  const [runtimeEnabled, setRuntimeEnabled] = useState(false);
  const [lastTest, setLastTest] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const [entityType, setEntityType] = useState('User');
  const [entityQ, setEntityQ] = useState('');
  const [entityLimit, setEntityLimit] = useState(20);
  const [entities, setEntities] = useState<any[]>([]);
  const [isQuerying, setIsQuerying] = useState(false);
  const [viewEntity, setViewEntity] = useState<any>(null);

  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<any[]>([]);
  const [businessSearch, setBusinessSearch] = useState('');
  const [businessResults, setBusinessResults] = useState<any[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const notify = (text: string, ok: boolean) => {
    setMessage({ text, ok });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadPlatformStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await adminPlatformAPI.getStatus();
      const data = res.data.data;
      setPlatformStatus(data);
      if (data?.fiware?.lastTest) setLastTest(data.fiware.lastTest);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Failed to load platform status', false);
    } finally {
      setStatusLoading(false);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminPlatformAPI.getFiwareSettings();
      const { config, enabled } = res.data.data;
      setForm({
        enabled: config.enabled ?? false,
        cbUrl: config.cbUrl || '',
        service: config.service || 'cerere',
        servicePath: config.servicePath || '/t44',
        apiKey: '',
        subscriptionCallbackUrl: config.subscriptionCallbackUrl || '',
      });
      setHasApiKey(config.hasApiKey);
      setRuntimeEnabled(enabled);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Failed to load settings', false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadPlatformStatus();
  }, []);

  const handleRefreshAll = async () => {
    await Promise.all([loadSettings(), loadPlatformStatus()]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = { ...form };
      if (!payload.apiKey) delete payload.apiKey;
      const res = await adminPlatformAPI.saveFiwareSettings(payload);
      setRuntimeEnabled(res.data.data.enabled);
      setHasApiKey(res.data.data.config?.hasApiKey ?? hasApiKey);
      setForm((f) => ({ ...f, apiKey: '' }));
      notify('FIWARE settings saved', true);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Failed to save settings', false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async (useForm = false) => {
    setIsTesting(true);
    try {
      const res = await adminPlatformAPI.testFiwareConnection(useForm ? form : undefined);
      const result = res.data.data;
      setLastTest(result);
      notify(result.message, result.ok);
    } catch (err: any) {
      const msg = err.response?.data?.data?.message || err.response?.data?.message || 'Connection test failed';
      setLastTest({ ok: false, message: msg });
      notify(msg, false);
    } finally {
      setIsTesting(false);
    }
  };

  const handleQueryEntities = async () => {
    setIsQuerying(true);
    try {
      const res = await adminPlatformAPI.queryEntities({
        type: entityType,
        q: entityQ || undefined,
        limit: entityLimit,
      });
      setEntities(res.data.data.entities || []);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Failed to query entities', false);
    } finally {
      setIsQuerying(false);
    }
  };

  const handleViewEntity = async (id: string) => {
    try {
      const res = await adminPlatformAPI.getEntity(id);
      setViewEntity(res.data.data);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Failed to load entity', false);
    }
  };

  const searchUsers = async () => {
    if (!userSearch.trim()) return;
    try {
      const res = await adminPlatformAPI.searchUsers(userSearch);
      setUserResults(res.data.data || []);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Search failed', false);
    }
  };

  const searchBusinesses = async () => {
    if (!businessSearch.trim()) return;
    try {
      const res = await adminPlatformAPI.searchBusinesses(businessSearch);
      setBusinessResults(res.data.data || []);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Search failed', false);
    }
  };

  const syncUser = async (id: string) => {
    setIsSyncing(true);
    try {
      await adminPlatformAPI.syncUser(id);
      notify('User synced to Context Broker', true);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Sync failed', false);
    } finally {
      setIsSyncing(false);
    }
  };

  const syncBusiness = async (id: string) => {
    setIsSyncing(true);
    try {
      await adminPlatformAPI.syncBusiness(id);
      notify('Business synced to Context Broker', true);
    } catch (err: any) {
      notify(err.response?.data?.message || 'Sync failed', false);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleBulkSync = async () => {
    setBulkConfirmOpen(false);
    setIsSyncing(true);
    try {
      const res = await adminPlatformAPI.bulkSync(50);
      const r = res.data.data;
      notify(
        `Bulk sync: ${r.users.synced} users, ${r.businesses.synced} businesses`,
        r.users.failed + r.businesses.failed === 0
      );
    } catch (err: any) {
      notify(err.response?.data?.message || 'Bulk sync failed', false);
    } finally {
      setIsSyncing(false);
    }
  };

  const getEntityId = (entity: any) => entity.id || entity._id;
  const getEntityLabel = (entity: any) => {
    const email = entity.email?.value ?? entity.email;
    const name = entity.name?.value ?? entity.name;
    const username = entity.username?.value ?? entity.username;
    return email || name || username || getEntityId(entity);
  };

  const StatusBadge = ({ ok, label }: { ok: boolean; label: string }) => (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
        ok ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {label}
    </span>
  );

  if (loading && activeTab !== 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Network className="h-8 w-8 text-emerald-600" />
          <h1 className="text-2xl font-bold text-gray-900">Platform & FIWARE</h1>
        </div>
        <button
          type="button"
          onClick={handleRefreshAll}
          disabled={loading || statusLoading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${statusLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {message && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            message.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <StatusBadge ok={runtimeEnabled} label={runtimeEnabled ? 'FIWARE enabled' : 'FIWARE disabled'} />
        {lastTest && (
          <StatusBadge
            ok={lastTest.ok}
            label={`${lastTest.ok ? 'Connected' : 'Not connected'}${lastTest.latencyMs != null ? ` (${lastTest.latencyMs}ms)` : ''}`}
          />
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-6 -mb-px">
          {TABS.map((label, idx) => (
            <button
              key={label}
              type="button"
              onClick={() => setActiveTab(idx as TabIndex)}
              className={`pb-3 text-sm font-medium border-b-2 ${
                activeTab === idx
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Connections</h2>
            <button
              type="button"
              onClick={loadPlatformStatus}
              disabled={statusLoading}
              className="text-sm text-emerald-600 hover:text-emerald-700"
            >
              Refresh status
            </button>
          </div>
          {statusLoading && !platformStatus ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold">{platformStatus?.mongodb?.label || 'Local MongoDB (supplylink)'}</span>
                </div>
                <StatusBadge
                  ok={platformStatus?.mongodb?.connected}
                  label={platformStatus?.mongodb?.connected ? 'Connected' : 'Not connected'}
                />
                <p className="text-sm text-gray-500 mt-2">
                  {platformStatus?.mongodb?.host} / {platformStatus?.mongodb?.name}
                </p>
                {platformStatus?.mongodb?.pingMs != null && (
                  <p className="text-sm text-gray-600">Ping: {platformStatus.mongodb.pingMs}ms</p>
                )}
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold">{platformStatus?.t43Mongo?.label || 'Identity MongoDB (bpa)'}</span>
                </div>
                <StatusBadge
                  ok={platformStatus?.t43Mongo?.connected}
                  label={platformStatus?.t43Mongo?.connected ? 'Connected' : 'Not connected'}
                />
                <p className="text-sm text-gray-500 mt-2">
                  {platformStatus?.t43Mongo?.host} / {platformStatus?.t43Mongo?.name}
                </p>
                {platformStatus?.t43Mongo?.pingMs != null && (
                  <p className="text-sm text-gray-600">Ping: {platformStatus.t43Mongo.pingMs}ms</p>
                )}
                <p className="text-sm text-gray-600">
                  Users: {platformStatus?.t43Mongo?.counts?.users ?? 0} · Businesses:{' '}
                  {platformStatus?.t43Mongo?.counts?.businesses ?? 0}
                </p>
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Cloud className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold">Orion Context Broker</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge ok={platformStatus?.fiware?.enabled} label={platformStatus?.fiware?.enabled ? 'Enabled' : 'Disabled'} />
                  {platformStatus?.fiware?.lastTest && (
                    <StatusBadge
                      ok={platformStatus.fiware.lastTest.ok}
                      label={
                        platformStatus.fiware.lastTest.ok
                          ? `OK (${platformStatus.fiware.lastTest.latencyMs ?? '?'}ms)`
                          : 'Unreachable'
                      }
                    />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2 truncate">
                  {platformStatus?.fiware?.config?.service}
                  {platformStatus?.fiware?.config?.servicePath}
                </p>
                <p className="text-sm text-gray-500 truncate">{platformStatus?.fiware?.config?.cbUrl || '—'}</p>
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <span className="font-semibold block mb-2">Identity reads</span>
                <p className="text-sm text-gray-600">Primary: {platformStatus?.identityReads?.primary}</p>
                <p className="text-sm text-gray-600">Fallback: {platformStatus?.identityReads?.fallback}</p>
              </div>

              <div className="rounded-xl border border-gray-100 p-4 md:col-span-2">
                <span className="font-semibold block mb-2">Platform auth</span>
                <StatusBadge
                  ok={platformStatus?.platform?.jwtSecretConfigured}
                  label={
                    platformStatus?.platform?.jwtSecretConfigured
                      ? 'JWT secret configured'
                      : 'JWT secret not configured'
                  }
                />
                <p className="text-sm text-gray-500 mt-2">
                  Canonical roles: {(platformStatus?.platform?.roles || ACCOUNT_TYPES).join(', ')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <p className="text-sm text-gray-500">
            Configure the shared CERERE FIWARE Context Broker. Settings are stored in the database and apply immediately.
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="rounded border-gray-300 text-emerald-600"
            />
            <span className="text-sm font-medium">Enabled</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Context Broker URL</label>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
              value={form.cbUrl}
              onChange={(e) => setForm({ ...form, cbUrl: e.target.value })}
              placeholder="https://cerere.unical.it/fiware"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">FIWARE Service</label>
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service path</label>
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2"
                value={form.servicePath}
                onChange={(e) => setForm({ ...form, servicePath: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <input
              type="password"
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
              value={form.apiKey}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
              placeholder={hasApiKey ? 'Leave blank to keep current key' : ''}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription callback URL</label>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
              value={form.subscriptionCallbackUrl}
              onChange={(e) => setForm({ ...form, subscriptionCallbackUrl: e.target.value })}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
            <button
              type="button"
              onClick={() => handleTestConnection(true)}
              disabled={isTesting}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <FlaskConical className="h-4 w-4" />
              Test connection
            </button>
          </div>
        </div>
      )}

      {activeTab === 2 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
            <select
              className="rounded-lg border border-gray-200 px-3 py-2"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
            >
              <option value="User">User</option>
              <option value="Business">Business</option>
            </select>
            <input
              className="sm:col-span-2 rounded-lg border border-gray-200 px-3 py-2"
              placeholder='NGSI query e.g. role=="farmer"'
              value={entityQ}
              onChange={(e) => setEntityQ(e.target.value)}
            />
            <input
              type="number"
              className="rounded-lg border border-gray-200 px-3 py-2"
              value={entityLimit}
              min={1}
              max={100}
              onChange={(e) => setEntityLimit(Number(e.target.value))}
            />
          </div>
          <button
            type="button"
            onClick={handleQueryEntities}
            disabled={isQuerying || !runtimeEnabled}
            className="mb-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            Query
          </button>
          {!runtimeEnabled && (
            <p className="text-amber-700 text-sm mb-4">Enable FIWARE and save settings before querying.</p>
          )}
          {entities.length === 0 ? (
            <p className="text-gray-500 text-sm">No entities returned.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {entities.map((entity) => {
                const id = getEntityId(entity);
                return (
                  <li key={id} className="flex justify-between items-center py-3">
                    <div>
                      <p className="font-medium text-gray-900">{getEntityLabel(entity)}</p>
                      <p className="text-xs text-gray-500">
                        {entity.type?.value ?? entity.type} · {id}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleViewEntity(id)}
                      className="p-2 text-gray-500 hover:text-emerald-600"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {activeTab === 3 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-8">
          <section>
            <h3 className="font-semibold text-gray-900 mb-3">Sync user to Context Broker</h3>
            <div className="flex gap-2 mb-3">
              <input
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2"
                placeholder="Search by email or username"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
              />
              <button type="button" onClick={searchUsers} className="px-3 border rounded-lg">
                <Search className="h-4 w-4" />
              </button>
            </div>
            {userResults.map((u) => (
              <div key={u._id} className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm">
                  {u.email} ({u.username})
                </span>
                <button
                  type="button"
                  onClick={() => syncUser(u._id)}
                  disabled={isSyncing || !runtimeEnabled}
                  className="inline-flex items-center gap-1 text-sm text-emerald-600 disabled:opacity-50"
                >
                  <RotateCw className="h-4 w-4" />
                  Sync
                </button>
              </div>
            ))}
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-3">Sync business to Context Broker</h3>
            <div className="flex gap-2 mb-3">
              <input
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2"
                placeholder="Search by name or email"
                value={businessSearch}
                onChange={(e) => setBusinessSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchBusinesses()}
              />
              <button type="button" onClick={searchBusinesses} className="px-3 border rounded-lg">
                <Search className="h-4 w-4" />
              </button>
            </div>
            {businessResults.map((b) => (
              <div key={b._id} className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm">
                  {b.name} ({b.email})
                </span>
                <button
                  type="button"
                  onClick={() => syncBusiness(b._id)}
                  disabled={isSyncing || !runtimeEnabled}
                  className="inline-flex items-center gap-1 text-sm text-emerald-600 disabled:opacity-50"
                >
                  <RotateCw className="h-4 w-4" />
                  Sync
                </button>
              </div>
            ))}
          </section>

          <div>
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={isSyncing || !runtimeEnabled}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
            >
              Bulk sync (max 50)
            </button>
            <p className="text-xs text-gray-500 mt-2">Sync up to 50 users and 50 businesses from bpa to Orion.</p>
          </div>
        </div>
      )}

      {viewEntity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-xl">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-semibold">Entity details</h3>
              <button type="button" onClick={() => setViewEntity(null)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <pre className="p-4 text-xs overflow-auto max-h-[60vh]">{JSON.stringify(viewEntity, null, 2)}</pre>
          </div>
        </div>
      )}

      {bulkConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="font-semibold mb-2">Bulk sync</h3>
            <p className="text-sm text-gray-600 mb-4">Sync up to 50 users and 50 businesses to Orion?</p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setBulkConfirmOpen(false)} className="px-4 py-2 border rounded-lg">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkSync}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlatform;
